import type { SurfacePlatform } from "@ui/hooks/platform";
import type { SheetDismissalPolicy, SheetPresentation } from "@ui/primitives/Sheet";
import { createContext, use } from "react";
import type { SurfaceChrome, SurfaceSize } from "./sheet-surface.styles";

export interface SheetSurfaceValue {
  /** The resolved skin, latched for as long as the surface is mounted. */
  platform: SurfacePlatform;
  size: SurfaceSize;
  chrome: SurfaceChrome;
  dismissal: SheetDismissalPolicy;
  /** Mobile only: a drag grabber replaces the header's nav control. */
  grabber: boolean;
  close: () => void;
  /** @internal Plumbing between the body's scroll sentinel and the footer's gradient. */
  isAtBottom: boolean;
  setIsAtBottom: (value: boolean) => void;
  /** @internal The footer registers itself so the body knows whether to pad its bottom. */
  hasFooter: boolean;
  setHasFooter: (value: boolean) => void;
}

/**
 * Defaults let the chrome parts render standalone (e.g. in stories of dialog
 * *content* components) with the desktop styling. Inside a surface the real
 * values win.
 */
const standalone: SheetSurfaceValue = {
  chrome: "card",
  close: () => {},
  dismissal: { clickOutside: true, dismissible: true, keyboard: true, swipe: true },
  grabber: false,
  hasFooter: false,
  isAtBottom: true,
  platform: "desktop",
  setHasFooter: () => {},
  setIsAtBottom: () => {},
  size: "sm",
};

export const SheetSurfaceContext = createContext<SheetSurfaceValue>(standalone);

/** Read the surrounding surface's state (or standalone defaults outside one). */
export function useSheetSurface(): SheetSurfaceValue {
  return use(SheetSurfaceContext);
}

export interface SheetSurfaceInternalValue {
  mounted: boolean;
  presentation: SheetPresentation;
}

/** @internal Plumbing between SheetSurface.Root and SheetSurface.View. Null outside a root. */
export const SheetSurfaceInternalContext = createContext<SheetSurfaceInternalValue | null>(null);
